import { useSnapshot } from 'valtio'
import { FlagBadge, RingBadge } from '@/components/Badge/Badge'
import { Link } from '@/lib/navigation'
import { state } from '@/lib/state'
import { cn } from '@/lib/utils'
import type { Flag, Item } from '../../../../types'
import styles from './ItemList.module.css'

export interface ItemListProps {
  items: readonly Item[]
  activeId?: string
  size?: 'small' | 'default' | 'large'
  hideRing?: boolean
  className?: string
}

export function ItemList({
  items,
  activeId,
  size = 'default',
  hideRing = false,
  className
}: ItemListProps) {
  const { radarConfig } = useSnapshot(state)
  const quadrants = radarConfig?.quadrants || []
  return (
    <ul
      className={cn(styles.list, className, {
        [styles.isSmall]: size === 'small',
        [styles.isLarge]: size === 'large'
      })}
      data-testid="item-list"
    >
      {items.map(item => {
        const quadrant = quadrants.find(q => q.id === item.quadrant)
        const latestRelease = item.releases.reduce((latest, current) => {
          if (!latest) return current
          return new Date(current.releaseDate) > new Date(latest.releaseDate)
            ? current
            : latest
        }, item.releases[0])

        return (
          <li
            className={styles.item}
            key={item.id}
            data-testid={`item-${item.id}`}
          >
            <Link
              className={cn(styles.link, {
                [styles.isActive]: item.id === activeId,
                'opacity-50': item.archived
              })}
              href={`${item.quadrant}/${item.id}`}
              data-testid={`item-${item.id}-actions`}
            >
              {size === 'large' ? (
                <span className={styles.titleWrapper}>
                  {!hideRing && (
                    <span className={styles.ringCol}>
                      <RingBadge
                        className={styles.ring}
                        ring={latestRelease.ring}
                        size="small"
                      />
                    </span>
                  )}
                  <span className={styles.title}>{latestRelease.title}</span>
                  {item.archived && (
                    <span className="ml-2 text-xs text-[var(--ds-text-subtle)] italic">
                      {' (Archived)'}
                    </span>
                  )}
                </span>
              ) : (
                <span className={styles.title}>
                  {latestRelease.title}
                  {item.archived && (
                    <span className="ml-2 text-xs text-[var(--ds-text-subtle)] italic">
                      {' (Archived)'}
                    </span>
                  )}
                </span>
              )}
              <FlagBadge
                className={styles.flag}
                flag={latestRelease.flag as Flag}
                outlined={true}
                short={size === 'small'}
              />

              {size === 'large' && (
                <div className={styles.info}>
                  <span className={styles.quadrant}>{quadrant?.title}</span>
                </div>
              )}
            </Link>
          </li>
        )
      })}
    </ul>
  )
}
